import { useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import { Toaster } from './components/ui/sonner';

// Lazy load everything below the fold
const ForderungsRechner = lazy(() => import('./components/ForderungsRechner'));
const TrustLogosSlider = lazy(() => import('./components/TrustLogosSlider'));
const StatsCounter = lazy(() => import('./components/StatsCounter'));
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const LogoSlider = lazy(() => import('./components/LogoSlider'));
const BenefitsSection = lazy(() => import('./components/BenefitsSection'));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection'));
const UrgencyBanner = lazy(() => import('./components/UrgencyBanner'));
const ProcessSection = lazy(() => import('./components/ProcessSection'));
const AnsprechpartnerSection = lazy(() => import('./components/AnsprechpartnerSection'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const Footer = lazy(() => import('./components/Footer'));
const CursorGlow = lazy(() => import('./components/CursorGlow'));
const StickyMobileCTA = lazy(() => import('./components/StickyMobileCTA'));
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const FAQPage = lazy(() => import('./pages/FAQ'));

// Minimal loading fallback - invisible, no layout shift
const Blank = () => null;

function LandingPage() {
  useEffect(() => {
    // Lightweight IntersectionObserver for fade animations
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    // Observe after a brief delay to let lazy components mount
    const timer = setTimeout(() => {
      document.querySelectorAll('.fade-in-up, .fade-in-left, .fade-in-right, .scale-in')
        .forEach((el) => io.observe(el));
    }, 100);

    // Re-observe periodically for lazy-loaded content
    const interval = setInterval(() => {
      document.querySelectorAll('.fade-in-up:not(.visible), .fade-in-left:not(.visible), .fade-in-right:not(.visible)')
        .forEach((el) => io.observe(el));
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      io.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <Suspense fallback={<Blank />}>
        <ForderungsRechner />
        <TrustLogosSlider />
        <StatsCounter />
        <ServicesSection />
        <LogoSlider />
        <BenefitsSection />
        <TestimonialsSection />
        <UrgencyBanner />
        <ProcessSection />
        <AnsprechpartnerSection />
        <ContactForm />
        <Footer />
      </Suspense>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<Blank />}>
        <CursorGlow />
        <StickyMobileCTA />
        <CookieBanner />
      </Suspense>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/impressum" element={<Suspense fallback={<Blank />}><Impressum /></Suspense>} />
        <Route path="/datenschutz" element={<Suspense fallback={<Blank />}><Datenschutz /></Suspense>} />
        <Route path="/faq" element={<Suspense fallback={<Blank />}><FAQPage /></Suspense>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
